import { useState, useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'

/**
 * Componente reutilizable para mostrar dashboards Grafana
 * @param {Object} props
 * @param {string} props.dashboardUid - UUID del dashboard (por defecto: '6cb7e678-82e6-405b-8680-cc2bb4718334')
 * @param {string} props.factory - Código de fábrica
 * @param {string} props.prodLine - Código de línea de producción
 * @param {number} props.assetId - ID del activo seleccionado (opcional)
 * @param {string} props.theme - Tema del dashboard (por defecto: 'dark')
 * @param {string} props.from - Rango de tiempo desde (por defecto: 'now-6h')
 * @param {string} props.to - Rango de tiempo hasta (por defecto: 'now')
 * @param {string} props.extruder - Código del extrusor (opcional)
 * @param {string} props.machine - Código de la máquina (opcional)
*/
export function DashboardViewer({
  dashboardUid = '6cb7e678-82e6-405b-8680-cc2bb4718334',
  factory,
  prodLine,
  assetId,
  extruder, 
  machine,   
  theme = 'dark',
  from = 'now-6h',
  to = 'now',
}) {
  const [iframeUrl, setIframeUrl] = useState(null)
  const [iframeKey, setIframeKey] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const iframeRef = useRef(null)

  // Base URL de Grafana
  const grafanaBaseUrl = import.meta.env.VITE_API_URL

  useEffect(() => {
    // Solo construir URL si tenemos el dashboard UID
    if (!dashboardUid) {
      setIframeUrl(null)
      setLoading(false)
      return
    }

    // Construir el slug del título desde el UID o usar 'dashboard' por defecto
    const titleSlug = 'dashboard'

    // Construir la URL manualmente para que kiosk aparezca sin valor (igual que Monitor.jsx)
    // Esto evita que URLSearchParams genere kiosk= en lugar de kiosk
    let queryParams = `orgId=1&kiosk&theme=${theme}&from=${from}&to=${to}`
    
    // Agregar variables si están disponibles
    if (factory) {
      queryParams += `&var-factory=${encodeURIComponent(factory)}`
    }
    if (prodLine) {
      queryParams += `&var-ProdLine=${encodeURIComponent(prodLine)}`
    }
    if (assetId) {
      queryParams += `&var-assetId=${assetId}`
    }
    if (assetId) {
      queryParams += `&var-assetId=${assetId}`
    }
    if (extruder) {  // ← Agregar
      queryParams += `&var-Extruder=${encodeURIComponent(extruder)}`
    }
    if (machine) {   // ← Agregar
      queryParams += `&var-Machine=${encodeURIComponent(machine)}`
    }

    // Construir la URL completa
    const newUrl = `${grafanaBaseUrl}/d/${dashboardUid}/${titleSlug}?${queryParams}`
    
    // Solo recargar iframe si la URL cambió realmente
    setIframeUrl(prevUrl => {
      if (prevUrl !== newUrl) {
        setIframeKey(prev => prev + 1)
      }
      return newUrl
    })
    setError(null)
  }, [dashboardUid, factory, prodLine, assetId, extruder, machine, theme, from, to])

  const refreshIframe = () => {
    setIframeKey(prev => prev + 1)
  }

  const openInNewTab = () => {
    if (iframeUrl) {
      window.open(iframeUrl, '_blank')
    }
  }

  if (!iframeUrl) {  // Cambiar de if (loading) a if (!iframeUrl)
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={refreshIframe}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!iframeUrl) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <p className="text-lg mb-4">No hay dashboard disponible</p>
          <p className="text-sm">Selecciona un activo del nivel 3 para ver el dashboard</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Controles del dashboard */}
      <div className="flex items-center justify-between p-2 border-b border-gray-700 bg-gray-800/50">
        <div className="text-sm text-gray-400">
          {factory && <span>Fábrica: <strong className="text-white">{factory}</strong></span>}
          {prodLine && (
            <span className="ml-4">
              Línea: <strong className="text-white">{prodLine}</strong>
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshIframe}
            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white transition-colors"
            title="Actualizar dashboard"
          >
            🔄 Actualizar
          </button>
          <button
            onClick={openInNewTab}
            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded text-white transition-colors"
            title="Abrir en nueva pestaña"
          >
            ↗ Pantalla completa
          </button>
        </div>
      </div>

      {/* Iframe del dashboard */}
      <div className="flex-1 min-h-0">
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={iframeUrl}
          title="Grafana Dashboard"
          className="w-full h-full border-0"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          allow="fullscreen"
          onLoad={() => setLoading(false)}
          onError={() => {
            setError('Error al cargar el dashboard')
            setLoading(false)
          }}
        />
      </div>
    </div>
  )
}